import { useMemo } from "react";
import {
  useListAccountMemberships,
  useListInventoryPlans,
  type Membership,
  type InventoryPlan,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";

/** INR formatter — mirrors account-detail.tsx's formatINR. */
function formatINR(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount)}`;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return format(new Date(d), "d MMM yyyy");
}

/**
 * Member detail page (Phase 37) — plan cards for a single contact.
 *
 * Reuses the LOCKED quoteGroupId grouping from account-detail.tsx's
 * `membershipGroups` (key = m.quoteGroupId ?? `solo-${m.id}`) so a
 * multi-space-type deal added together in one Add Plan collapses into ONE
 * "Plan #{first-line membership id}" card — same nomenclature used
 * everywhere else in the app (Plan tab, Transactions, Credit Notes,
 * Security Deposits). A solo plan is its own card ("Plan #{id}").
 *
 * Do NOT re-derive this grouping elsewhere — extend here if the rule ever
 * changes.
 */
export function MemberPlanCards({
  accountId,
  contactId,
}: {
  accountId: number;
  contactId: number;
}) {
  const { data: memberships, isLoading } = useListAccountMemberships(accountId);
  const { data: plans = [] } = useListInventoryPlans();

  const planById = useMemo(() => {
    const map = new Map<number, InventoryPlan>();
    for (const p of plans) map.set(p.id, p);
    return map;
  }, [plans]);

  const groups = useMemo(() => {
    const mine = ((memberships ?? []) as Membership[]).filter(
      (m) => m.contactId === contactId,
    );
    const map = new Map<string, Membership[]>();
    for (const m of mine) {
      const key = m.quoteGroupId ?? `solo-${m.id}`;
      (map.get(key) ?? map.set(key, []).get(key)!).push(m);
    }
    return Array.from(map.entries());
  }, [memberships, contactId]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">— No active plan</p>;
  }

  return (
    <div className="space-y-3">
      {groups.map(([groupKey, group]) => {
        // "First-line" membership id — same convention as the Plan tab's
        // group-row title (account-detail.tsx: `Plan #{group[0]!.id}`).
        const firstLine = group[0]!;
        const spaceTypes = group
          .map(
            (m) =>
              `${m.quantity && m.quantity > 1 ? `${m.quantity}× ` : ""}${
                planById.get(m.planId)?.planName.replace(/_/g, " ") ?? `PLN-${m.planId}`
              }${m.durationMonths ? ` · ${m.durationMonths} mo` : ""}`,
          )
          .join(" | ");
        const sameEnd = new Set(group.map((m) => m.endDate ?? "")).size === 1;
        const endLabel = sameEnd ? fmtDate(firstLine.endDate) : "Mixed";
        const totalDeposit = group.reduce(
          (sum, m) => sum + Number(m.securityDepositHeld ?? 0),
          0,
        );
        const statusUniform = new Set(group.map((m) => m.status)).size === 1;

        return (
          <Card key={groupKey}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm font-semibold">
                  Plan #{firstLine.id}
                </CardTitle>
                {statusUniform ? (
                  <Badge
                    variant={firstLine.status === "active" ? "default" : "secondary"}
                    className="capitalize"
                  >
                    {firstLine.status}
                  </Badge>
                ) : (
                  <Badge variant="secondary">Mixed</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{spaceTypes}</p>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-muted-foreground">Term</span>
                <span className="text-right font-medium">
                  {fmtDate(firstLine.startDate)} – {endLabel}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-muted-foreground">Deposit</span>
                <span className="font-medium tabular-nums">{formatINR(totalDeposit)}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
